<template>
  <div class="view-container">
    <div class="compose-layout">
      <div class="compose-header">
        <h3><i class="fas fa-pen"></i> {{ isReply ? '回复邮件' : isDraft ? '编辑草稿' : '撰写邮件' }}</h3>
        <button @click="goBack" class="btn-close"><i class="fas fa-times"></i></button>
      </div>

      <div class="compose-body">
        <div class="form-section">
          <div class="form-row">
            <label class="form-label required">收件人</label>
            <div class="recipient-input">
              <input 
                v-model="form.toAccount" 
                class="form-control" 
                placeholder="请输入收件人账号"
                @blur="fetchRecipientName"
              >
              <button @click="showUserSelector" class="btn-select"><i class="fas fa-address-book"></i> 选择</button>
            </div>
          </div>

          <div class="form-row" v-if="form.toName">
            <label class="form-label">收件人姓名</label>
            <span class="recipient-name">{{ form.toName }}</span>
          </div>

          <div class="form-row">
            <label class="form-label required">主题</label>
            <input v-model="form.subject" class="form-control" placeholder="请输入邮件主题">
          </div>

          <div class="form-row full">
            <label class="form-label required">内容</label>
            <div class="editor-wrapper">
              <div class="editor-toolbar">
                <button @click="insertBold" class="toolbar-btn" title="粗体"><i class="fas fa-bold"></i></button>
                <button @click="insertItalic" class="toolbar-btn" title="斜体"><i class="fas fa-italic"></i></button>
                <button @click="insertUnderline" class="toolbar-btn" title="下划线"><i class="fas fa-underline"></i></button>
                <span class="toolbar-divider"></span>
                <button @click="insertList" class="toolbar-btn" title="列表"><i class="fas fa-list-ul"></i></button>
                <button @click="insertLink" class="toolbar-btn" title="链接"><i class="fas fa-link"></i></button>
                <span class="toolbar-divider"></span>
                <button @click="clearFormat" class="toolbar-btn" title="清除格式"><i class="fas fa-eraser"></i></button>
              </div>
              <textarea 
                ref="contentEditor"
                v-model="form.content" 
                class="form-textarea"
                placeholder="请输入邮件内容..."
                rows="15"
              ></textarea>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">附件</label>
            <div class="attachment-section">
              <div v-for="(file, index) in attachments" :key="index" class="attachment-item">
                <i class="fas fa-paperclip"></i>
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                <button @click="removeAttachment(index)" class="btn-remove"><i class="fas fa-times"></i></button>
              </div>
              <button @click="triggerFileUpload" class="btn-upload">
                <i class="fas fa-cloud-upload-alt"></i> 添加附件
              </button>
              <input 
                ref="fileInput" 
                type="file" 
                multiple 
                style="display: none" 
                @change="handleFileSelect"
              >
            </div>
          </div>
        </div>
      </div>

      <div class="compose-footer">
        <div class="footer-left">
          <button @click="sendMail" class="btn btn-primary" :disabled="loading">
            <i class="fas fa-paper-plane"></i> {{ loading ? '发送中...' : '发送' }}
          </button>
          <button @click="saveDraft" class="btn btn-default" :disabled="loading">
            <i class="fas fa-save"></i> 保存草稿
          </button>
        </div>
        <div class="footer-right">
          <button @click="goBack" class="btn btn-default">
            <i class="fas fa-times"></i> 取消
          </button>
        </div>
      </div>
    </div>

    <!-- 用户选择器弹窗 -->
    <div v-if="showUserModal" class="modal-overlay" @click.self="showUserModal = false">
      <div class="modal-dialog" style="width: 600px;">
        <div class="modal-header">
          <h4>选择收件人</h4>
          <button @click="showUserModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="search-box">
            <input v-model="userSearch" class="form-control" placeholder="搜索用户...">
          </div>
          <div class="user-list">
            <div 
              v-for="user in filteredUsers" 
              :key="user.account" 
              class="user-item"
              @click="selectUser(user)"
            >
              <div class="user-avatar" :style="{ background: user.color }">{{ user.avatar }}</div>
              <div class="user-info">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-account">{{ user.account }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showUserModal = false" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { sendMail as sendMailAPI, saveDraft as saveDraftAPI } from '../api/mail'

const router = useRouter()
const route = useRoute()

const loading = ref(false)
const isReply = ref(false)
const isDraft = ref(false)
const showUserModal = ref(false)
const userSearch = ref('')
const contentEditor = ref(null)
const fileInput = ref(null)

const form = reactive({
  id: null,
  toAccount: '',
  toName: '',
  subject: '',
  content: ''
})

const attachments = ref([])

// 模拟用户列表（实际应该从后端获取）
const users = ref([
  { account: 'admin', name: '管理员', avatar: '管', color: '#3b82f6' },
  { account: 'zhangsan', name: '张三', avatar: '张', color: '#10b981' },
  { account: 'lisi', name: '李四', avatar: '李', color: '#f59e0b' },
  { account: 'wangwu', name: '王五', avatar: '王', color: '#8b5cf6' },
  { account: 'zhaoliu', name: '赵六', avatar: '赵', color: '#ef4444' }
])

const filteredUsers = computed(() => {
  if (!userSearch.value) return users.value
  const q = userSearch.value.toLowerCase()
  return users.value.filter(u => 
    u.account.toLowerCase().includes(q) || 
    u.name.toLowerCase().includes(q)
  )
})

/**
 * 获取收件人姓名
 */
const fetchRecipientName = () => {
  if (!form.toAccount) return
  const user = users.value.find(u => u.account === form.toAccount)
  if (user) {
    form.toName = user.name
  }
}

/**
 * 显示用户选择器
 */
const showUserSelector = () => {
  showUserModal.value = true
}

/**
 * 选择用户
 */
const selectUser = (user) => {
  form.toAccount = user.account
  form.toName = user.name
  showUserModal.value = false
}

/**
 * 发送邮件
 */
const sendMail = async () => {
  // 验证
  if (!form.toAccount) {
    $message.warning('请输入收件人')
    return
  }
  if (!form.subject.trim()) {
    $message.warning('请输入邮件主题')
    return
  }
  if (!form.content.trim()) {
    $message.warning('请输入邮件内容')
    return
  }

  try {
    loading.value = true
    await sendMailAPI({
      toAccount: form.toAccount,
      toName: form.toName,
      subject: form.subject,
      content: form.content
    })
    
    $message.success('发送成功')
    goBack()
  } catch (error) {
    console.error('发送失败:', error)
    $message.error('发送失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 保存草稿
 */
const saveDraft = async () => {
  if (!form.subject.trim() && !form.content.trim()) {
    $message.error('主题和内容不能同时为空')
    return
  }

  try {
    loading.value = true
    await saveDraftAPI({
      id: form.id,
      toAccount: form.toAccount,
      toName: form.toName,
      subject: form.subject,
      content: form.content
    })
    
    $message.success('保存成功')
    goBack()
  } catch (error) {
    console.error('保存失败:', error)
    $message.error('保存失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 返回
 */
const goBack = () => {
  router.back()
}

/**
 * 编辑器工具栏功能
 */
const insertBold = () => {
  insertFormat('<b>', '</b>')
}

const insertItalic = () => {
  insertFormat('<i>', '</i>')
}

const insertUnderline = () => {
  insertFormat('<u>', '</u>')
}

const insertList = () => {
  const text = prompt('请输入列表项（用逗号分隔）')
  if (text) {
    const items = text.split(',').map(item => `<li>${item.trim()}</li>`).join('')
    insertText(`<ul>${items}</ul>`)
  }
}

const insertLink = () => {
  const url = prompt('请输入链接地址')
  if (url) {
    const text = prompt('请输入链接文本')
    insertText(`<a href="${url}">${text || url}</a>`)
  }
}

const clearFormat = async () => {
  if (!await $confirm('确定清除所有格式吗？')) {
    form.content = form.content.replace(/<[^>]*>/g, '')
  }
}

const insertFormat = (openTag, closeTag) => {
  const textarea = contentEditor.value
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const selectedText = form.content.substring(start, end)
  
  if (selectedText) {
    const newText = openTag + selectedText + closeTag
    form.content = form.content.substring(0, start) + newText + form.content.substring(end)
    
    // 重新设置光标位置
    setTimeout(() => {
      textarea.selectionStart = start
      textarea.selectionEnd = start + newText.length
      textarea.focus()
    }, 0)
  }
}

const insertText = (text) => {
  const textarea = contentEditor.value
  const start = textarea.selectionStart
  form.content = form.content.substring(0, start) + text + form.content.substring(start)
  
  setTimeout(() => {
    textarea.selectionStart = start + text.length
    textarea.selectionEnd = start + text.length
    textarea.focus()
  }, 0)
}

/**
 * 附件处理
 */
const triggerFileUpload = () => {
  fileInput.value.click()
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  attachments.value.push(...files)
  event.target.value = '' // 清空input以便重复选择同一文件
}

const removeAttachment = (index) => {
  attachments.value.splice(index, 1)
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * 初始化
 */
onMounted(() => {
  // 如果有路由参数，可以初始化表单
  if (route.query.to) {
    form.toAccount = route.query.to
    fetchRecipientName()
  }
  if (route.query.subject) {
    form.subject = route.query.subject
  }
  if (route.query.replyTo) {
    isReply.value = true
    form.subject = 'Re: ' + route.query.subject
  }
})
</script>

<style scoped>
.view-container { background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; max-height: calc(100vh - 110px); }
.compose-layout { display: flex; flex-direction: column; height: calc(100vh - 115px); }

.compose-header { padding: 16px 20px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; background: #fafbfc; }
.compose-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #1f2937; display: flex; align-items: center; gap: 8px; }
.btn-close { background: none; border: none; font-size: 20px; color: #9ca3af; cursor: pointer; padding: 4px 8px; transition: color 0.2s; }
.btn-close:hover { color: #374151; }

.compose-body { flex: 1; overflow-y: auto; padding: 20px; }
.form-section { max-width: 900px; margin: 0 auto; }
.form-row { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
.form-row.full { flex-direction: column; align-items: stretch; }
.form-label { min-width: 100px; font-size: 13px; font-weight: 500; color: #374151; }
.form-label.required::after { content: '*'; color: #ef4444; margin-left: 4px; }
.form-control { flex: 1; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; }
.form-control:focus { outline: none; border-color: #3b82f6; }
.form-textarea { width: 100%; padding: 12px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; font-family: inherit; resize: vertical; }
.form-textarea:focus { outline: none; border-color: #3b82f6; }

.recipient-input { flex: 1; display: flex; gap: 8px; }
.btn-select { padding: 8px 16px; background: #fff; color: #374151; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; font-size: 13px; white-space: nowrap; transition: all 0.2s; }
.btn-select:hover { background: #f9fafb; border-color: #3b82f6; color: #3b82f6; }
.recipient-name { font-size: 13px; color: #6b7280; }

.editor-wrapper { width: 100%; }
.editor-toolbar { display: flex; gap: 4px; padding: 8px; background: #f9fafb; border: 1px solid #d1d5db; border-bottom: none; border-radius: 4px 4px 0 0; }
.toolbar-btn { width: 32px; height: 32px; background: #fff; border: 1px solid #e5e7eb; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #6b7280; font-size: 14px; transition: all 0.2s; }
.toolbar-btn:hover { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.toolbar-divider { width: 1px; height: 24px; background: #e5e7eb; margin: 0 4px; }

.attachment-section { flex: 1; }
.attachment-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 8px; font-size: 13px; }
.file-name { flex: 1; color: #374151; }
.file-size { color: #9ca3af; font-size: 12px; }
.btn-remove { background: none; border: none; color: #ef4444; cursor: pointer; font-size: 16px; padding: 0 4px; }
.btn-remove:hover { color: #dc2626; }
.btn-upload { padding: 8px 16px; background: #fff; color: #3b82f6; border: 1px dashed #3b82f6; border-radius: 4px; cursor: pointer; font-size: 13px; transition: all 0.2s; }
.btn-upload:hover { background: #eff6ff; }

.compose-footer { padding: 12px 20px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; background: #fafbfc; }
.footer-left, .footer-right { display: flex; gap: 8px; }

.btn { padding: 8px 16px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }

/* 用户选择器弹窗 */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-dialog { background: #fff; border-radius: 8px; overflow: hidden; max-height: 85vh; display: flex; flex-direction: column; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h4 { margin: 0; font-size: 16px; }
.modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #999; }
.modal-close:hover { color: #333; }
.modal-body { padding: 20px; flex: 1; overflow-y: auto; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }

.search-box { margin-bottom: 12px; }
.user-list { max-height: 400px; overflow-y: auto; }
.user-item { display: flex; align-items: center; gap: 12px; padding: 10px; border-radius: 6px; cursor: pointer; transition: background 0.15s; }
.user-item:hover { background: #f9fafb; }
.user-avatar { width: 40px; height: 40px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0; }
.user-info { flex: 1; }
.user-name { font-size: 13px; font-weight: 500; color: #1f2937; margin-bottom: 2px; }
.user-account { font-size: 12px; color: #9ca3af; }
</style>
